import React, { useCallback, useEffect, useRef, useState } from 'react';

import { registerPlanningAction } from '../services/planningActionRegistry';
import { projectService } from '../services/projectService';
import type { SupervisorButton } from '../types/supervisorButtons';

import { recordActionClick, recordUserActionClick } from './AiOverviewPanel/useActionBarClicks';
import { ArtifactUpdatesKeepCurrent, keepCurrentTaskIds } from './ArtifactUpdatesKeepCurrent';
import { BrandPendulumMark } from './BrandPendulumMark';
import { usePlanningActionSelection } from './planningActionSelection';
import { showPlanningTasks } from './PlanningTasks';

interface ArtifactUpdatesActionProps {
  projectId?: string | null;
  sessionId?: string | null;
  entryId?: string;
  button: SupervisorButton;
  current?: boolean;
  variant?: 'overview' | 'chat';
}

const inFlightActions = new Set<string>();

/** Uses the saved selection invocation; artifact updates never resume the design pipeline. */
// eslint-disable-next-line max-lines-per-function
export const ArtifactUpdatesAction: React.FC<ArtifactUpdatesActionProps> = ({
  projectId, sessionId, entryId, button, current = true, variant = 'overview',
}) => {
  const selection = usePlanningActionSelection({
    projectId, entryId, phase: 'artifact_updates', buttons: [button], current,
  });
  const [pending, setPending] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const identity = useRef(selection.actionIdentity);
  const label = selection.labelFor(button.label);
  const enabled = Boolean(projectId && current && button.enabled !== false && selection.active
    && !selection.disabled && !pending && !accepted);

  useEffect(() => {
    identity.current = selection.actionIdentity;
    setPending(false);
    setAccepted(false);
    setError(null);
    inFlight.current = false;

    return () => { identity.current = ''; };
  }, [selection.actionIdentity]);

  // eslint-disable-next-line max-statements
  const invoke = useCallback(async () => {
    if (!enabled || !projectId || inFlight.current || inFlightActions.has(selection.actionIdentity)
      || !selection.selection?.task_ids.length) return;
    const requestIdentity = selection.actionIdentity;
    let invocationId = selection.preparedInvocationId;

    inFlight.current = true;
    inFlightActions.add(requestIdentity);
    setPending(true);
    setError(null);
    try {
      if (!invocationId) await recordUserActionClick(
        entryId, 'artifact_updates', button.key, label, projectId, sessionId,
        {
          required: true, planningSelection: selection.selection, baseLabel: button.label,
          onRecorded: (id) => {
            invocationId = id;
            selection.markPrepared(id, label);
          },
        },
      );
      if (!invocationId) throw new Error('The selected updates could not be prepared.');
      await projectService.runArtifactUpdates(projectId, invocationId);
      void recordActionClick(entryId, 'artifact_updates', button.key, projectId, sessionId);
      if (identity.current === requestIdentity) {
        showPlanningTasks(projectId);
        setAccepted(true);
      }
    } catch {
      if (identity.current === requestIdentity) setError('The selected updates could not be started. Try again.');
    } finally {
      inFlightActions.delete(requestIdentity);
      if (identity.current === requestIdentity) {
        inFlight.current = false;
        setPending(false);
      }
    }
  }, [button.key, button.label, enabled, entryId, label, projectId, selection, sessionId]);

  useEffect(() => {
    if (!projectId) return undefined;

    return registerPlanningAction({
      projectId, sessionId: sessionId ?? null, entryId: selection.actionIdentity,
      buttonKey: button.key, label, enabled, current, invoke,
    });
  }, [button.key, current, enabled, invoke, label, projectId, selection.actionIdentity, sessionId]);

  return <>
    <button
      className={variant === 'chat' ? 'system-msg-review-btn system-msg-review-btn--continue'
        : 'ai-overview-action-btn ai-overview-action-btn--continue'}
      type="button" disabled={!enabled} aria-busy={pending || undefined} onClick={invoke}
    >
      {pending && <BrandPendulumMark animated={false} className={variant === 'chat' ? 'review-btn-spinner' : 'ai-overview-action-spinner'} />}
      {pending ? 'Starting updates…' : accepted ? 'Updates queued' : label}
    </button>
    {error && <span className="ai-overview-action-error" role="alert">{error}</span>}
    {button.payload?.keep_current === true && <ArtifactUpdatesKeepCurrent
      projectId={projectId} entryId={entryId} taskIds={keepCurrentTaskIds(button)} variant={variant}
      enabled={Boolean(current && button.enabled !== false && selection.active && !pending && !accepted)}
    />}
  </>;
};
